import React, { useState, useEffect } from 'react';
import { Settings, Save, Plus, Trash2, GripVertical, Loader2 } from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../lib/firebase';
import { useAuth } from '../../context/AuthContext';
import PageHeader from '../../components/common/PageHeader';

interface LoyaltyTier {
  id: string;
  name: string;
  minPoints: number;
  pointsMultiplier: number;
  color: string;
}

interface GiftCardOption {
  id: string;
  name: string;
  enabled: boolean;
}

interface LoyaltySettings {
  programName: string;
  basePointsRate: number;
  pointsToDollarRate: number;
  minRedemptionPoints: number;
  maxRedemptionPointsPerDay: number;
  pointsExpiryMonths: number;
  multiLoanBonusEnabled: boolean;
  multiLoanBonusMultiplier: number;
  multiLoanMinLoans: number;
  highValueBonusEnabled: boolean;
  highValueBonusMultiplier: number;
  highValueMinAmountCents: number;
  referralBonusEnabled: boolean;
  referralBonusPoints: number;
  tiers: LoyaltyTier[];
  giftCards: GiftCardOption[];
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  subdomain: string;
  fulfillmentEmail: string;
}

const DEFAULT_TIERS: LoyaltyTier[] = [
  { id: 'bronze', name: 'Bronze', minPoints: 0, pointsMultiplier: 1.0, color: '#cd7f32' },
  { id: 'silver', name: 'Silver', minPoints: 2500, pointsMultiplier: 1.25, color: '#c0c0c0' },
  { id: 'gold', name: 'Gold', minPoints: 10000, pointsMultiplier: 1.5, color: '#c9a84c' },
  { id: 'platinum', name: 'Platinum', minPoints: 25000, pointsMultiplier: 2.0, color: '#e5e4e2' },
];

const DEFAULT_GIFT_CARDS: GiftCardOption[] = [
  { id: 'visa-debit-cash', name: 'Visa Debit Cash Card', enabled: true },
  { id: 'restaurant', name: 'Restaurant Card', enabled: true },
  { id: 'groceries', name: 'Groceries Card', enabled: true },
  { id: 'entertainment', name: 'Entertainment Card', enabled: true },
  { id: 'fuel', name: 'Fuel Card', enabled: true },
  { id: 'cafe', name: 'Cafe Card', enabled: true },
  { id: 'home-living', name: 'Home Living Card', enabled: true },
  { id: 'travel', name: 'Travel Card', enabled: true },
  { id: 'fashion', name: 'Fashion Card', enabled: true },
  { id: 'hardware', name: 'Hardware Card', enabled: true },
];

const DEFAULT_SETTINGS: LoyaltySettings = {
  programName: 'Entourage Rewards',
  basePointsRate: 0.10,
  pointsToDollarRate: 10,
  minRedemptionPoints: 200,
  maxRedemptionPointsPerDay: 5000,
  pointsExpiryMonths: 0,
  multiLoanBonusEnabled: true,
  multiLoanBonusMultiplier: 1.5,
  multiLoanMinLoans: 2,
  highValueBonusEnabled: false,
  highValueBonusMultiplier: 2.0,
  highValueMinAmountCents: 50000000,
  referralBonusEnabled: true,
  referralBonusPoints: 1000,
  tiers: DEFAULT_TIERS,
  giftCards: DEFAULT_GIFT_CARDS,
  primaryColor: '#003d24',
  secondaryColor: '#212121',
  accentColor: '#c9a84c',
  subdomain: 'entourage',
  fulfillmentEmail: 'rewards@entourage.com.au',
};

const LoyaltySettingsPage: React.FC = () => {
  const { companyId } = useAuth();
  const [settings, setSettings] = useState<LoyaltySettings>(DEFAULT_SETTINGS);
  const [saving, setSaving] = useState(false);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Load settings from Firestore on mount
  useEffect(() => {
    if (!companyId) {
      setLoadingSettings(false);
      return;
    }

    const loadSettings = async () => {
      try {
        // Read from root loyalty_configs collection (where backend scheduled functions read)
        const configRef = doc(db, 'loyalty_configs', companyId);
        const snapshot = await getDoc(configRef);

        if (snapshot.exists()) {
          const data = snapshot.data() as Partial<LoyaltySettings & {
            baseRate?: number;
            giftCardCategories?: string[];
            minimumRedemptionPoints?: number;
          }>;
          setSettings((prev) => ({
            ...prev,
            ...data,
            // Map backend field names to frontend field names
            basePointsRate: data.baseRate != null ? data.baseRate / 100 : (data.basePointsRate ?? prev.basePointsRate),
            minRedemptionPoints: data.minimumRedemptionPoints ?? (data.minRedemptionPoints ?? prev.minRedemptionPoints),
            tiers: Array.isArray(data.tiers) && data.tiers.length > 0 ? data.tiers : prev.tiers,
            giftCards: Array.isArray(data.giftCards) && data.giftCards.length > 0
              ? data.giftCards
              : Array.isArray(data.giftCardCategories) && data.giftCardCategories.length > 0
                ? data.giftCardCategories.map((cat: string) => {
                    const existing = prev.giftCards.find((gc) => gc.id === cat);
                    return existing ? { ...existing, enabled: true } : { id: cat, name: cat, enabled: true };
                  })
                : prev.giftCards,
          }));
        }
      } catch (err: any) {
        console.error('[LoyaltySettings] Failed to load settings:', err);
        setMessage({ type: 'error', text: 'Failed to load settings. Using defaults.' });
        setTimeout(() => setMessage({ type: '', text: '' }), 4000);
      } finally {
        setLoadingSettings(false);
      }
    };

    loadSettings();
  }, [companyId]);

  const handleSave = async () => {
    if (!companyId) {
      setMessage({ type: 'error', text: 'Company ID not found. Please refresh and try again.' });
      return;
    }

    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const updateConfigFn = httpsCallable<
        {
          companyId: string;
          subdomain?: string;
          baseRate?: number;
          fulfillmentEmail?: string;
          giftCardCategories?: string[];
          minimumRedemptionPoints?: number;
          companyName?: string;
        },
        { success: boolean }
      >(functions, 'updateLoyaltyConfig');

      await updateConfigFn({
        companyId,
        subdomain: settings.subdomain || undefined,
        // Backend expects baseRate as integer (cents-style, e.g., 10 = $0.10 per $1)
        baseRate: Math.round(settings.basePointsRate * 100),
        fulfillmentEmail: settings.fulfillmentEmail || undefined,
        giftCardCategories: settings.giftCards.filter((gc) => gc.enabled).map((gc) => gc.id),
        minimumRedemptionPoints: settings.minRedemptionPoints || undefined,
        companyName: settings.programName || undefined,
      });
      setMessage({ type: 'success', text: 'Loyalty settings saved successfully.' });
      setTimeout(() => setMessage({ type: '', text: '' }), 4000);
    } catch (err: any) {
      console.error('[LoyaltySettings] Failed to save settings:', err);
      const errorMessage = err?.message || err?.details || 'Unknown error';
      setMessage({ type: 'error', text: `Failed to save settings: ${errorMessage}` });
    } finally {
      setSaving(false);
    }
  };

  const updateTier = (index: number, field: keyof LoyaltyTier, value: string | number) => {
    setSettings((prev) => {
      const tiers = [...prev.tiers];
      tiers[index] = { ...tiers[index], [field]: value };
      return { ...prev, tiers };
    });
  };

  const addTier = () => {
    setSettings((prev) => ({
      ...prev,
      tiers: [
        ...prev.tiers,
        {
          id: `tier-${Date.now()}`,
          name: 'New Tier',
          minPoints: (prev.tiers[prev.tiers.length - 1]?.minPoints || 0) + 10000,
          pointsMultiplier: 1.0,
          color: '#6b7280',
        },
      ],
    }));
  };

  const removeTier = (index: number) => {
    if (settings.tiers.length <= 1) return;
    setSettings((prev) => ({
      ...prev,
      tiers: prev.tiers.filter((_, i) => i !== index),
    }));
  };

  const toggleGiftCard = (index: number) => {
    setSettings((prev) => {
      const giftCards = [...prev.giftCards];
      giftCards[index] = { ...giftCards[index], enabled: !giftCards[index].enabled };
      return { ...prev, giftCards };
    });
  };

  const enabledCardCount = settings.giftCards.filter((c) => c.enabled).length;

  const sectionClass = 'bg-gray-800/50 border border-gray-700/50 rounded-xl p-6';
  const labelClass = 'block text-sm font-medium text-gray-300 mb-2';
  const inputClass = 'w-full bg-gray-700 border border-gray-600 text-white rounded-md p-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500';
  const hintClass = 'text-xs text-gray-500 mt-1';

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Settings}
        title="Loyalty Settings"
        subtitle="Configure your client loyalty program — all settings are fully customisable"
        actions={
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed transition-colors"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        }
      />

      {message.text && (
        <div className={`p-3 rounded-lg text-sm ${message.type === 'success' ? 'bg-green-900/50 text-green-300 border border-green-500/30' : 'bg-red-900/50 text-red-300 border border-red-500/30'}`}>
          {message.text}
        </div>
      )}

      {loadingSettings ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
          <span className="ml-3 text-sm text-gray-400">Loading loyalty settings...</span>
        </div>
      ) : (
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Program Details */}
        <div className={sectionClass}>
          <h3 className="text-lg font-semibold text-white mb-4">Program Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="program-name" className={labelClass}>Program Name</label>
              <input
                id="program-name"
                type="text"
                value={settings.programName}
                onChange={(e) => setSettings((prev) => ({ ...prev, programName: e.target.value }))}
                className={inputClass}
                placeholder="e.g., Entourage Rewards"
              />
              <p className={hintClass}>Displayed on the loyalty portal and all client communications</p>
            </div>
            <div>
              <label htmlFor="subdomain" className={labelClass}>Portal Subdomain</label>
              <div className="flex items-center gap-0">
                <input
                  id="subdomain"
                  type="text"
                  value={settings.subdomain}
                  onChange={(e) => setSettings((prev) => ({ ...prev, subdomain: e.target.value.replace(/[^a-z0-9-]/gi, '').toLowerCase() }))}
                  className="bg-gray-700 border border-gray-600 border-r-0 text-white rounded-l-md p-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 w-48"
                  placeholder="rewards"
                />
                <span className="bg-gray-600 border border-gray-600 text-gray-400 rounded-r-md p-3 text-sm">.valutten.com</span>
              </div>
              <p className={hintClass}>
                Client portal: <span className="text-blue-400">{settings.subdomain || 'rewards'}.valutten.com</span>
              </p>
            </div>
          </div>
        </div>

        {/* Points Configuration */}
        <div className={sectionClass}>
          <h3 className="text-lg font-semibold text-white mb-4">Points Configuration</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="base-rate" className={labelClass}>Base Points Rate</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                <input
                  id="base-rate"
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={settings.basePointsRate}
                  onChange={(e) => setSettings((prev) => ({ ...prev, basePointsRate: parseFloat(e.target.value) || 0 }))}
                  className={`${inputClass} pl-7`}
                />
              </div>
              <p className={hintClass}>Points earned per $1 of trail commission (e.g., 0.10 = $0.10 per $1)</p>
            </div>
            <div>
              <label htmlFor="pts-dollar" className={labelClass}>Points-to-Dollar Rate</label>
              <input
                id="pts-dollar"
                type="number"
                min="1"
                step="1"
                value={settings.pointsToDollarRate}
                onChange={(e) => setSettings((prev) => ({ ...prev, pointsToDollarRate: parseInt(e.target.value) || 1 }))}
                className={inputClass}
              />
              <p className={hintClass}>Points needed for $1 gift card value (e.g., 10 pts = $1)</p>
            </div>
            <div>
              <label htmlFor="min-redeem" className={labelClass}>Minimum Redemption Points</label>
              <input
                id="min-redeem"
                type="number"
                min="1"
                step="1"
                value={settings.minRedemptionPoints}
                onChange={(e) => setSettings((prev) => ({ ...prev, minRedemptionPoints: parseInt(e.target.value) || 0 }))}
                className={inputClass}
              />
              <p className={hintClass}>Minimum points to make a redemption</p>
            </div>
            <div>
              <label htmlFor="max-redeem" className={labelClass}>Max Redemption Per Day</label>
              <input
                id="max-redeem"
                type="number"
                min="0"
                step="100"
                value={settings.maxRedemptionPointsPerDay}
                onChange={(e) => setSettings((prev) => ({ ...prev, maxRedemptionPointsPerDay: parseInt(e.target.value) || 0 }))}
                className={inputClass}
              />
              <p className={hintClass}>0 = unlimited. Limits daily redemption to prevent fraud</p>
            </div>
            <div>
              <label htmlFor="expiry" className={labelClass}>Points Expiry (months)</label>
              <input
                id="expiry"
                type="number"
                min="0"
                step="1"
                value={settings.pointsExpiryMonths}
                onChange={(e) => setSettings((prev) => ({ ...prev, pointsExpiryMonths: parseInt(e.target.value) || 0 }))}
                className={inputClass}
              />
              <p className={hintClass}>0 = never expire. Otherwise points expire after N months of inactivity</p>
            </div>
          </div>
        </div>

        {/* Bonus Multipliers */}
        <div className={sectionClass}>
          <h3 className="text-lg font-semibold text-white mb-4">Bonus Multipliers</h3>
          <div className="space-y-6">
            {/* Multi-loan bonus */}
            <div className="flex items-start gap-4">
              <label className="relative inline-flex items-center cursor-pointer mt-1">
                <input
                  type="checkbox"
                  checked={settings.multiLoanBonusEnabled}
                  onChange={() => setSettings((prev) => ({ ...prev, multiLoanBonusEnabled: !prev.multiLoanBonusEnabled }))}
                  className="sr-only peer"
                />
                <div className="w-9 h-5 bg-gray-600 peer-focus:ring-2 peer-focus:ring-blue-500 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600" />
              </label>
              <div className="flex-1">
                <p className="text-sm font-medium text-white">Multi-Loan Bonus</p>
                <p className={hintClass}>Reward clients with multiple active loans</p>
                {settings.multiLoanBonusEnabled && (
                  <div className="grid grid-cols-2 gap-4 mt-3">
                    <div>
                      <label className="text-xs text-gray-400">Multiplier</label>
                      <input
                        type="number"
                        step="0.1"
                        min="1"
                        value={settings.multiLoanBonusMultiplier}
                        onChange={(e) => setSettings((prev) => ({ ...prev, multiLoanBonusMultiplier: parseFloat(e.target.value) || 1 }))}
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-400">Minimum Loans</label>
                      <input
                        type="number"
                        min="2"
                        value={settings.multiLoanMinLoans}
                        onChange={(e) => setSettings((prev) => ({ ...prev, multiLoanMinLoans: parseInt(e.target.value) || 2 }))}
                        className={inputClass}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* High-value bonus */}
            <div className="flex items-start gap-4">
              <label className="relative inline-flex items-center cursor-pointer mt-1">
                <input
                  type="checkbox"
                  checked={settings.highValueBonusEnabled}
                  onChange={() => setSettings((prev) => ({ ...prev, highValueBonusEnabled: !prev.highValueBonusEnabled }))}
                  className="sr-only peer"
                />
                <div className="w-9 h-5 bg-gray-600 peer-focus:ring-2 peer-focus:ring-blue-500 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600" />
              </label>
              <div className="flex-1">
                <p className="text-sm font-medium text-white">High-Value Loan Bonus</p>
                <p className={hintClass}>Extra points for clients with loans above a threshold</p>
                {settings.highValueBonusEnabled && (
                  <div className="grid grid-cols-2 gap-4 mt-3">
                    <div>
                      <label className="text-xs text-gray-400">Multiplier</label>
                      <input
                        type="number"
                        step="0.1"
                        min="1"
                        value={settings.highValueBonusMultiplier}
                        onChange={(e) => setSettings((prev) => ({ ...prev, highValueBonusMultiplier: parseFloat(e.target.value) || 1 }))}
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-400">Min Loan Amount ($)</label>
                      <input
                        type="number"
                        min="0"
                        step="10000"
                        value={settings.highValueMinAmountCents / 100}
                        onChange={(e) => setSettings((prev) => ({ ...prev, highValueMinAmountCents: Math.round((parseFloat(e.target.value) || 0) * 100) }))}
                        className={inputClass}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Referral bonus */}
            <div className="flex items-start gap-4">
              <label className="relative inline-flex items-center cursor-pointer mt-1">
                <input
                  type="checkbox"
                  checked={settings.referralBonusEnabled}
                  onChange={() => setSettings((prev) => ({ ...prev, referralBonusEnabled: !prev.referralBonusEnabled }))}
                  className="sr-only peer"
                />
                <div className="w-9 h-5 bg-gray-600 peer-focus:ring-2 peer-focus:ring-blue-500 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600" />
              </label>
              <div className="flex-1">
                <p className="text-sm font-medium text-white">Referral Bonus</p>
                <p className={hintClass}>Bonus points when a client refers someone who joins the program</p>
                {settings.referralBonusEnabled && (
                  <div className="w-48 mt-3">
                    <label className="text-xs text-gray-400">Bonus Points</label>
                    <input
                      type="number"
                      min="0"
                      step="100"
                      value={settings.referralBonusPoints}
                      onChange={(e) => setSettings((prev) => ({ ...prev, referralBonusPoints: parseInt(e.target.value) || 0 }))}
                      className={inputClass}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Loyalty Tiers */}
        <div className={sectionClass}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-white">Loyalty Tiers</h3>
              <p className="text-sm text-gray-400 mt-1">Define tier levels based on lifetime points earned. Each tier can have a points multiplier.</p>
            </div>
            <button
              onClick={addTier}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-blue-600/20 text-blue-400 border border-blue-500/30 hover:bg-blue-600/30 transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
              Add Tier
            </button>
          </div>

          <div className="space-y-3">
            {settings.tiers.map((tier, index) => (
              <div
                key={tier.id}
                className="flex items-center gap-3 p-4 bg-gray-700/30 border border-gray-600/50 rounded-lg group"
              >
                <GripVertical className="w-4 h-4 text-gray-600 flex-shrink-0" />
                <input
                  type="color"
                  value={tier.color}
                  onChange={(e) => updateTier(index, 'color', e.target.value)}
                  className="w-8 h-8 rounded border border-gray-600 cursor-pointer bg-transparent flex-shrink-0"
                />
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 flex-1">
                  <div>
                    <label className="text-[10px] uppercase text-gray-500 tracking-wider">Name</label>
                    <input
                      type="text"
                      value={tier.name}
                      onChange={(e) => updateTier(index, 'name', e.target.value)}
                      className="w-full bg-gray-600 border border-gray-500 text-white rounded p-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase text-gray-500 tracking-wider">Min Points</label>
                    <input
                      type="number"
                      min="0"
                      value={tier.minPoints}
                      onChange={(e) => updateTier(index, 'minPoints', parseInt(e.target.value) || 0)}
                      className="w-full bg-gray-600 border border-gray-500 text-white rounded p-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase text-gray-500 tracking-wider">Multiplier</label>
                    <input
                      type="number"
                      step="0.1"
                      min="1"
                      value={tier.pointsMultiplier}
                      onChange={(e) => updateTier(index, 'pointsMultiplier', parseFloat(e.target.value) || 1)}
                      className="w-full bg-gray-600 border border-gray-500 text-white rounded p-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                  <div className="flex items-end">
                    <div
                      className="flex items-center gap-2 px-3 py-2 rounded text-xs font-semibold"
                      style={{ backgroundColor: `${tier.color}20`, color: tier.color, border: `1px solid ${tier.color}40` }}
                    >
                      <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: tier.color }} />
                      {tier.name} &middot; {tier.pointsMultiplier}x
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => removeTier(index)}
                  disabled={settings.tiers.length <= 1}
                  className="text-gray-600 hover:text-red-400 disabled:opacity-30 disabled:cursor-not-allowed transition-colors flex-shrink-0"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>

          {/* Tier Preview */}
          <div className="mt-4 p-4 bg-gray-900/50 rounded-lg">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-3">Tier Progress Preview</p>
            <div className="flex items-center gap-1">
              {settings.tiers.map((tier, index) => {
                const nextTier = settings.tiers[index + 1];
                const width = nextTier
                  ? ((nextTier.minPoints - tier.minPoints) / (settings.tiers[settings.tiers.length - 1]?.minPoints || 1)) * 100
                  : 20;
                return (
                  <div key={tier.id} className="flex-1" style={{ maxWidth: `${Math.max(width, 15)}%` }}>
                    <div className="h-2 rounded-full mb-1" style={{ backgroundColor: tier.color }} />
                    <div className="text-[10px] text-gray-500">
                      <span style={{ color: tier.color }} className="font-medium">{tier.name}</span>
                      <br />{tier.minPoints.toLocaleString()} pts
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Gift Card Categories */}
        <div className={sectionClass}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-white">Gift Card Types</h3>
              <p className="text-sm text-gray-400 mt-1">Select which gift card types are available to your clients</p>
            </div>
            <span className="text-xs text-gray-400">{enabledCardCount} of {settings.giftCards.length} enabled</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {settings.giftCards.map((card, index) => (
              <label
                key={card.id}
                className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                  card.enabled
                    ? 'border-blue-500/50 bg-blue-500/10'
                    : 'border-gray-600/50 bg-gray-700/30 hover:border-gray-500/50'
                }`}
              >
                <input
                  type="checkbox"
                  checked={card.enabled}
                  onChange={() => toggleGiftCard(index)}
                  className="w-4 h-4 text-blue-600 bg-gray-600 border-gray-500 rounded focus:ring-blue-500"
                />
                <span className={`text-sm ${card.enabled ? 'text-white' : 'text-gray-400'}`}>{card.name}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Branding */}
        <div className={sectionClass}>
          <h3 className="text-lg font-semibold text-white mb-4">Branding</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className={labelClass}>Primary Color</label>
              <div className="flex items-center gap-3">
                <input type="color" value={settings.primaryColor} onChange={(e) => setSettings((prev) => ({ ...prev, primaryColor: e.target.value }))} className="w-10 h-10 rounded-md border border-gray-600 cursor-pointer bg-transparent" />
                <input type="text" value={settings.primaryColor} onChange={(e) => setSettings((prev) => ({ ...prev, primaryColor: e.target.value }))} className="flex-1 bg-gray-700 border border-gray-600 text-white rounded-md p-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-blue-500" />
              </div>
            </div>
            <div>
              <label className={labelClass}>Secondary Color</label>
              <div className="flex items-center gap-3">
                <input type="color" value={settings.secondaryColor} onChange={(e) => setSettings((prev) => ({ ...prev, secondaryColor: e.target.value }))} className="w-10 h-10 rounded-md border border-gray-600 cursor-pointer bg-transparent" />
                <input type="text" value={settings.secondaryColor} onChange={(e) => setSettings((prev) => ({ ...prev, secondaryColor: e.target.value }))} className="flex-1 bg-gray-700 border border-gray-600 text-white rounded-md p-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-blue-500" />
              </div>
            </div>
            <div>
              <label className={labelClass}>Accent / Gold Color</label>
              <div className="flex items-center gap-3">
                <input type="color" value={settings.accentColor} onChange={(e) => setSettings((prev) => ({ ...prev, accentColor: e.target.value }))} className="w-10 h-10 rounded-md border border-gray-600 cursor-pointer bg-transparent" />
                <input type="text" value={settings.accentColor} onChange={(e) => setSettings((prev) => ({ ...prev, accentColor: e.target.value }))} className="flex-1 bg-gray-700 border border-gray-600 text-white rounded-md p-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-blue-500" />
              </div>
            </div>
          </div>
          {/* Preview */}
          <div className="mt-4 rounded-lg p-4 border border-gray-600/50" style={{ backgroundColor: settings.primaryColor }}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold" style={{ backgroundColor: settings.accentColor, color: settings.primaryColor }}>R</div>
              <span className="font-semibold text-white">{settings.programName || 'Rewards'}</span>
            </div>
            <div className="flex gap-2">
              <span className="px-3 py-1 rounded-md text-white text-xs font-medium" style={{ backgroundColor: settings.secondaryColor }}>Secondary</span>
              <span className="px-3 py-1 rounded-md text-xs font-medium" style={{ backgroundColor: settings.accentColor, color: settings.primaryColor }}>Accent</span>
            </div>
          </div>
        </div>

        {/* Notifications */}
        <div className={sectionClass}>
          <h3 className="text-lg font-semibold text-white mb-4">Notifications</h3>
          <div>
            <label htmlFor="fulfillment-email" className={labelClass}>Fulfillment Notification Emails</label>
            <textarea
              id="fulfillment-email"
              value={settings.fulfillmentEmail}
              onChange={(e) => setSettings((prev) => ({ ...prev, fulfillmentEmail: e.target.value }))}
              className={`${inputClass} resize-y min-h-[80px]`}
              placeholder="rewards@yourcompany.com.au, admin@yourcompany.com.au"
              rows={2}
            />
            <p className={hintClass}>Comma-separated. All addresses receive notifications when clients submit redemption requests</p>
          </div>
        </div>

        {/* Save */}
        <div className="flex justify-end pt-2 pb-4">
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed transition-colors"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
      )}
    </div>
  );
};

export default LoyaltySettingsPage;
